/** A serviced city, and the branch the contact tab opens when it is picked. */
export interface ProfessionalCityLink {
    city: string;
    locationID: number;
}

interface CityLocation {
    ID: number;
    City?: string | null;
    LocationHidden?: boolean | null;
}

/**
 * The cities a professional services, each paired with the branch a reader
 * lands on when they pick it.
 *
 * The rows that draw these show a city once, but a franchise can hold several
 * branches in one city — Resene runs dozens — so the city carries the first
 * branch listed there. Hidden locations are left out, as everywhere else the
 * list is drawn.
 *
 * Shared by the desktop meta row and the mobile summary so the two can never
 * disagree about which branch a city opens.
 */
export const professionalCityLinks = (
    locations: readonly CityLocation[] | null | undefined,
): ProfessionalCityLink[] => {
    const byCity = new Map<string, ProfessionalCityLink>();
    for (const location of locations ?? []) {
        if (location.LocationHidden) continue;
        const city = location.City?.trim();
        if (!city || byCity.has(city)) continue;
        byCity.set(city, { city, locationID: location.ID });
    }
    return [...byCity.values()];
};

/**
 * The contact tab with `locationID` already selected.
 *
 * `ID` is the key the contact page reads to settle on a branch, and the revamp
 * opts out of auto-selecting the first one — so arriving without it opens
 * zoomed out over every pin, and arriving with it opens on that branch.
 *
 * `tabBase` comes from `professionalTabBase`, so the link stays inside
 * whichever tree is mounted rather than throwing the Business Manager preview
 * out onto the public site.
 */
export const professionalLocationHref = (
    tabBase: string,
    locationID: number,
): string => `${tabBase}/contact?ID=${locationID}`;
